import {ReasonInputType} from '../ReasonInputType';
import {CreateDisputeReasonProps} from './commonReasons';

export enum PendingAdditionalVerification {
  /**
   * Plaid or Finicity verification
   */
  deposit,
  /**
   * PersonaId verification
   */
  persona,
}

export const PENDING_REASON_USE_DRIVER_LICENSE = 'Upload Drivers License';
export const PENDING_REASON_USE_PERSONA = 'Persona';

/* Pending Reasons with Select Input Type may have different details for each param */
export type ParamsDetailsFields = {
  description: string;
  text: string;
  info: string;
};

export type ParamsDetails = Record<string, ParamsDetailsFields>;

export type PendingReasonSettings = CreateDisputeReasonProps & {
  title: string;
  additionalVerification?: PendingAdditionalVerification;
  text: string;
  document?: string;
  info: string;
  paramsDetails?: ParamsDetails;
};

export enum MerchantDocumentsPendingApplication {
  depositAccountVerification = 'Deposit Account Verification',
  einVerification = 'Tax Identification Number (EIN) Verification',
  ssnVerification = 'Social Security Number Verification',
  proofOfPayments = 'Tax Liens Proof of Payment',
  letterOfAttestation = 'Letter Of Attestation',
  marketingMaterials = 'Marketing Materials',
  proofOfPhysicalLocation = 'Proof of Physical Location',
  monthlyProcessingLimits = 'Monthly Processing Limits',
  singleTransactionLimits = 'Single Transaction Limits',
  stateOfGoodStanding = 'Secretary of State Good Standing',
  otherDocuments = 'Other Documents',
  driversLicense = 'Driver License',
  sampleContract = 'Sample Contract',
  proofOfFulfillment = 'Proof of Fulfillment',
  processingStatements = 'Processing Statement',
  businessBankStatements = 'Business Bank Statement',
  personalBankStatements = 'Personal Bank Statement',
  creditReportInquiries = 'Credit Report',
  businessNotLinked = 'Business Not Linked',
  creditCardForm = 'Credit Card Form',
  legitScriptIdentifier = 'LegitScript Identifier',
  ecomAdsMaterials = 'Ecom Ads Materials',
}

export const UrlToOpenTemplate: string = '{url_to_open}';
export const LEGITSCRIPT_REQUIRED_REASON_NAME = '5912 - LegitScript Identifier';

/*
  description: for the email
  text: for the Portal documents upload page
*/
export const PendingReasonsRaw = [
  {
    name: 'Unable to Validate Banking',
    description: `We are unable to validate the bank deposit account provided. Please provide one of the following:
* Pre-Printed Voided Check with DBA or Legal name
* Bank letter containing the account and routing number and business name
* Bank statement showing the complete account number and routing number
  `,
    type: ReasonInputType.Checkbox,
    title: 'Deposit Account Verification',
    additionalVerification: PendingAdditionalVerification.deposit,
    text: 'Click here to upload a voided check, bank letter, or bank statement issued from your bank',
    document: MerchantDocumentsPendingApplication.depositAccountVerification,
    info: `The voided check, bank letter, or bank statements must be printed from your bank displaying your company name with the corresponding routing number and account number provided.`,
  },
  {
    name: 'Unable to Validate EIN',
    description: `We are unable to validate the Tax Identification Number (EIN) of the legal entity. Please provide the correct EIN# or the IRS SS-4 letter`,
    type: ReasonInputType.Checkbox,
    title: 'Tax Identification Number (EIN) Verification',
    text: `Click here to upload a copy of the applicant's IRS SS-4 letter displaying the company name and 9 digit Tax Identification Number (EIN) issued from the IRS or please provide the correct number below.`,
    document: MerchantDocumentsPendingApplication.einVerification,
    info: `The IRS SS-4 letter must be legible, include all pages, and be in the business name provided.`,
  },
  {
    name: 'Unable to Validate SSN',
    description: `We are unable to validate the Social Security Number provided. Please provide one of the following:
  * Applicant’s SSN card
  * First page of applicant’s personal tax return
  `,
    type: ReasonInputType.Checkbox,
    title: 'Social Security Number Verification',
    text: `Click here to upload a copy of the applicant's Social Security Card or the first page of the personal tax return displaying the name and social security number provided.`,
    document: MerchantDocumentsPendingApplication.ssnVerification,
    info: `The Social Security Card or first page of the personal tax return must be legible and be in the applicant's name provided.`,
  },
  {
    name: 'Credit Report Fraud Alert',
    description: `The applicant’s credit profile shows a fraud alert has been added. We will need to clear the fraud alert by calling the merchant at the phone number listed on the credit report. Please advise of a date and timeframe for us to make an outbound call.`,
    type: ReasonInputType.Checkbox,
    title: 'Identity Verification',
    text: `The applicant's credit profile shows a fraud alert. Please call our offices at 866-849-2445 to confirm your identity per your request.`,
    info: `This security verification is to help protect and validate your identity per your request.`,
  },
  {
    name: 'Credit Report Frozen',
    description: `The applicant currently has a security freeze on their credit profile. Please contact the credit bureau Experian to request a temporary lift by calling 800-509-8495 x2, x1, x1, x1. Please advise once this has been completed so we may attempt to access the applicant's credit profile.`,
    type: ReasonInputType.Checkbox,
    title: 'Identity Security Frozen',
    text: `The applicant's credit profile has a security freeze. Please contact the credit bureau Experian to request a temporary lift by calling 800-509-8495 x2, x1, x1, x1. Please advise once this has been completed so we may attempt to access the applicant's credit profile.`,
    info: `This security verification is to help protect and validate your identity per your request.`,
  },
  {
    name: 'Merchant Reserve Agreement',
    description: `Please acknowledge a merchant reserve agreement will be applied that reflects the following terms:`,
    type: ReasonInputType.RichText,
    title: 'Merchant Reserve Agreement',
    text: `Please acknowledge a merchant reserve agreement will be applied that reflects the following terms: <a href=${UrlToOpenTemplate} rel='noreferrer' target='_blank'>Merchant Reserve Agreement Terms & Conditions</a>`,
    info: `Our credit team has determined that we are able to approve your account with a merchant reserve to help offset the increased risk exposure on your account. The acknowledgement of the merchant reserve will approve your account. As you process with good performance and build a positive history, your account will be re-evaluated to possibly reduce or remove the merchant reserve.`,
  },
  {
    name: 'Tax Liens Proof of Payment',
    description: `Please provide proof of payment, payment plan, or status of current lien(s).`,
    type: ReasonInputType.Checkbox,
    title: 'Tax Liens Proof of Payment',
    text: `Please provide proof of payment, payment plan, or status of current lien(s).`,
    document: MerchantDocumentsPendingApplication.proofOfPayments,
    info: `The tax lien proof of payment documentation must be legible, include all pages, and be in the applicant's name or business name provided.`,
  },
  {
    name: 'Letter of Attestation',
    description: `Please provide a letter of attestation regarding the following:`,
    type: ReasonInputType.RichText,
    title: 'Letter of Attestation',
    text: `Click here to upload a signed letter stating the attached`,
    document: MerchantDocumentsPendingApplication.letterOfAttestation,
    info: `The letter of attestation is required for compliance due to the nature of the industry your business is in.`,
  },
  {
    name: 'Marketing Materials',
    description: `Merchants that are MOTO are required to provide a description of all products and services they offer. Please provide the following:
* Company marketing material
* Picture(s) of the inventory
* Detailed description addressing the timeframe of the delivery methods, order process, billing process and shipping process
* Sample contract provided to the consumer including the refund policy
* Price points and fee schedule for all products & services offered`,
    type: ReasonInputType.Checkbox,
    title: 'Marketing Materials',
    text: `Click here to upload a copy of the following:\n
* Company marketing material\n
* Picture(s) of the inventory\n
* Detailed description addressing the timeframe of the delivery methods, order process, billing process and shipping process\n
* Sample contract provided to the consumer including the refund policy\n
* Price points and fee schedule for all products & services offered`,
    document: MerchantDocumentsPendingApplication.marketingMaterials,
    info: `The marketing material documents must be legible and be in the applicant's name or business name provided.`,
  },
  {
    name: 'Proof of Physical Location',
    description: `We are unable to validate the applicant's business online through websites, marketing sites, reviews, images, etc. Please provide the merchant's current business license and/or store lease agreement`,
    type: ReasonInputType.Checkbox,
    title: 'Proof of Physical Location',
    text: `Click here to upload a copy of the applicant's business license and/or store lease agreement.`,
    document: MerchantDocumentsPendingApplication.proofOfPhysicalLocation,
    info: `The business license or store lease agreement must be legible and be in the applicant's name or business name provided.`,
  },
  {
    name: 'Website Updates',
    description: `The website appears to be under construction or has not been completed with all required compliance policies:`,
    type: ReasonInputType.RichText,
    title: 'Website Updates',
    text: `The applicant's website is missing required regulatory documentation. Please advise once all items have been confirmed visible on the website:\n
* Home Page\n
* Contact Page\n
* Terms and Conditions\n
* Privacy Policy\n
* Refund Policy\n
* Shipping Policy (if applicable)\n
* Products/Ingredients List (if applicable)`,
    info: `The following updates to your website must be updated to reflect this specific online store. If using a template, please make sure to update everything to match your business name, and include an email for the business.`,
  },
  {
    name: 'Request for Docs - AMV',
    description: `Due to the high average monthly volume, please provide one of the following:
* 3 months most recent business bank statements
* 3 months most recent processing statements
* 2 years most recent business tax returns, P&L, and Balance Sheet
  `,
    type: ReasonInputType.Checkbox,
    title: 'Higher Monthly Processing Limit',
    additionalVerification: PendingAdditionalVerification.deposit,
    text: `Click here to upload a copy of one of the following:\n
* 3 months most recent business bank statements in business name\n
* 3 months most recent processing statements in business name\n
* 2 years most recent business tax returns, P&L, and Balance Sheet in business name`,
    document: MerchantDocumentsPendingApplication.monthlyProcessingLimits,
    info: `Bank statements, processing statement, business tax returns, P&L, and balance sheets must be legible, include all pages, and be in the business name provided.`,
  },
  {
    name: 'Request for Docs - High Ticket',
    description: `Due to the high ticket amount, please provide one of the following:
* 3 months most recent business bank statements
* 3 months most recent processing statements
* 2 years most recent business tax returns, P&L, and Balance Sheet
  `,
    type: ReasonInputType.Checkbox,
    title: 'Higher Single Transaction Limit',
    additionalVerification: PendingAdditionalVerification.deposit,
    text: `Click here to upload a copy of one of the following:\n
* 3 months most recent business bank statements in business name\n
* 3 months most recent processing statements in business name\n
* 2 years most recent business tax returns, P&L, and Balance Sheet in business name`,
    document: MerchantDocumentsPendingApplication.singleTransactionLimits,
    info: `Bank statements, processing statement, business tax returns, P&L, and balance sheets must be legible, include all pages, and be in the business name provided.`,
  },
  {
    name: 'Secretary of State Good Standing',
    description: `The applicant's business Secretary of State shows that the registration is either past due or they are not in good standing/pending inactive. Please provide proof of reinstatement or good standing.`,
    type: ReasonInputType.Checkbox,
    title: 'Secretary of State Good Standing',
    text: `Click here to upload a copy of the applicant's business Secretary of State reinstatement or good standing.`,
    document: MerchantDocumentsPendingApplication.stateOfGoodStanding,
    info: `The Secretary of State good standing is required for compliance by your state. The reinstatement or good standing letter must be legible, include all pages, and be in the business name provided.`,
  },

  {
    name: 'Failed KBA',
    type: ReasonInputType.Select,
    title: 'Failed KBA',
    description: '',
    text: '',
    info: '',
    additionalVerification: PendingAdditionalVerification.persona,
    params: {
      label: 'Select verification type',
      values: [PENDING_REASON_USE_DRIVER_LICENSE, PENDING_REASON_USE_PERSONA],
    },
    document: MerchantDocumentsPendingApplication.driversLicense,
    paramsDetails: {
      [PENDING_REASON_USE_DRIVER_LICENSE]: {
        description: `Due to the identity verification not being completed, please provide an unexpired Driver’s License.`,
        text: `Due to the identity verification not being completed, please provide an unexpired Driver’s License.`,
        info: `The Driver’s License photo must be legible and in the signer’s name provided.`,
      },
      [PENDING_REASON_USE_PERSONA]: {
        description: `Due to the identity verification not being completed, the signer must complete the Persona identity verification process.`,
        text: `Due to the identity verification not being completed, the signer must complete the Persona identity verification process.`,
        info: `The Driver’s License photo must be legible and in the signer's name provided.`,
      },
    },
  },
  {
    name: 'Sample Contract',
    description: `Due to the merchant not selling online, please provide a Sample Contract that details the terms, privacy policy, refund policy and method of delivery.`,
    type: ReasonInputType.Checkbox,
    title: 'Sample Contract',
    text: `Due to the merchant not selling online, please provide a Sample Contract that details the terms, privacy policy, refund policy and method of delivery.`,
    document: MerchantDocumentsPendingApplication.sampleContract,
    info: `This form must be legible, be in the business name provided, and include the terms a customer would be agreeing to for the products or services you are selling.`,
  },
  {
    name: 'Proof of Fulfillment',
    description: `Please provide evidence of a relationship with a fulfillment company or your ability to fulfill the products advertised.`,
    type: ReasonInputType.Checkbox,
    title: 'Proof of Fulfillment',
    text: `Please provide evidence of a relationship with a fulfillment company or your ability to fulfill the products advertised.`,
    document: MerchantDocumentsPendingApplication.proofOfFulfillment,
    info: `The evidence of fulfillment is to demonstrate you have an established vendor who currently is or can fulfill customer orders. Screenshots can help us verify this.`,
  },
  {
    name: 'Processing Statements',
    description: `Please provide the most recent 3 months processing statements or a login to Stripe/Shopify for us to grab screenshots of the dashboard showing sales & disputes.`,
    type: ReasonInputType.Checkbox,
    title: 'Processing Statements',
    text: `Please provide the most recent 3 months processing statements or a login to Stripe/Shopify for us to grab screenshots of the dashboard showing sales & disputes.`,
    document: MerchantDocumentsPendingApplication.processingStatements,
    info: `Processing statements must be legible, include all pages, and be in the business name provided. A Stripe/Shopify login must have the financials and orders modules enabled.`,
  },
  {
    name: 'Business Bank Statements',
    description: `Please provide the most recent 3 months business bank statements.`,
    type: ReasonInputType.Checkbox,
    title: 'Business Bank Statements',
    text: `Please provide the most recent 3 months business bank statements.`,
    document: MerchantDocumentsPendingApplication.businessBankStatements,
    info: `Business bank statements must be legible, include all pages, and be in the business name provided.`,
  },
  {
    name: 'Personal Bank Statements',
    description: `Please provide the most recent 3 months personal bank statements.`,
    type: ReasonInputType.Checkbox,
    title: 'Personal Bank Statements',
    text: `Please provide the most recent 3 months personal bank statements.`,
    document: MerchantDocumentsPendingApplication.personalBankStatements,
    info: `Personal Bank statements must be legible, include all pages, and be in the applicant's name provided.`,
  },
  {
    name: 'Trial Language',
    description: `The website has trial verbiage which is prohibited. All trial verbiage will need to be removed before we can continue our Underwriting review.`,
    type: ReasonInputType.Checkbox,
    title: 'Trial Language',
    text: `The website has trial verbiage which is prohibited. All trial verbiage will need to be removed before we can continue our Underwriting review.`,
    info: `We are unable to support any trial offers as it requires a high risk registration with Visa. If you wish to proceed without this, please remove all evidence of trial from your site and terms and conditions.`,
  },
  {
    name: 'Credit Report Inquiries',
    description: `The credit report shows a recent inquiry with a merchant processor. Please confirm the terms of the account if opened.`,
    type: ReasonInputType.Checkbox,
    title: 'Credit Report Inquiries',
    text: `The credit report shows a recent inquiry with a merchant processor. Please confirm the terms of the account if opened.`,
    document: MerchantDocumentsPendingApplication.creditReportInquiries,
    info: `Please advise of all recent merchant processors you’ve applied with in the last 90 days and the results to align with what we’re seeing.`,
  },
  {
    name: 'Business Not Linked',
    description: `We are unable to link the applicant with the business. Please provide any of the following documentation: Articles of Organization, Fully Executed Operating Agreement, Fully Executed Lease Agreement, or Business License that shows the application as the business owner.`,
    type: ReasonInputType.Checkbox,
    title: 'Business Not Linked',
    text: `We are unable to link the applicant with the business. Please provide any of the following documentation: Articles of Organization, Fully Executed Operating Agreement, Fully Executed Lease Agreement, or Business License that shows the application as the business owner.`,
    document: MerchantDocumentsPendingApplication.businessNotLinked,
    info: `This form must be legible, be in the business name provided, and include the applicant’s name.`,
  },
  {
    name: 'Credit Card Form',
    description: `Please provide a copy of the credit card authorization form.`,
    type: ReasonInputType.Checkbox,
    title: 'Credit Card Form',
    text: `Please provide a copy of the credit card authorization form.`,
    document: MerchantDocumentsPendingApplication.creditCardForm,
    info: `This form must be legible, be in the business name provided, and include the terms and conditions authorizing the sale.`,
  },
  {
    name: '5993 - High Integrity Risk',
    description: `This merchant has been identified as 5993 MCC,
Please clone this application and select the correct MCC code, the merchant will be required to accept this High Integrity Risk Acknowledgment in the new application:
"By stating that 100% of sales will be swiped/in person, the merchant is attesting that they will not key any sales using this merchant account. If any keyed sales are identified, the merchant agrees to be registered with Visa under the High Integrity Risk program, which is required for merchants categorized as 5993 - Tobacco, Cigarettes, Cigar stores, Vape e-cigs, e-cigarettes conducting card not present sales. The merchant will be assessed the $950 Visa registration fee (recurring annually). The merchant will also be responsible for any Non-Compliance Assessments levied by Visa."

No response is required, once the new cloned application has been submitted, this application will be withdrawn.`,
    type: ReasonInputType.Checkbox,
    title: '5993 - High Integrity Risk',
    text: `This merchant has been identified as 5993 MCC,
Please clone this application and select the correct MCC code, the merchant will be required to accept this High Integrity Risk Acknowledgment in the new application:
"By stating that 100% of sales will be swiped/in person, the merchant is attesting that they will not key any sales using this merchant account. If any keyed sales are identified, the merchant agrees to be registered with Visa under the High Integrity Risk program, which is required for merchants categorized as 5993 - Tobacco, Cigarettes, Cigar stores, Vape e-cigs, e-cigarettes conducting card not present sales. The merchant will be assessed the $950 Visa registration fee (recurring annually). The merchant will also be responsible for any Non-Compliance Assessments levied by Visa."`,
    info: `No response is required, once the new cloned application has been submitted, this application will be withdrawn.`,
  },
  {
    name: LEGITSCRIPT_REQUIRED_REASON_NAME,
    description: `The application states that the merchant expects some percentage of keyed sales. For keyed pharmacy sales, per Visa, the merchant must provide proof of one of the following certifications/accreditations in order to approve the merchant account. We’ll need confirmation that the merchant is accredited via one of the following programs:
* The National Association of Boards of Pharmacy (NABP) in either the Healthcare Merchant Accreditation or Drug Distributor Accreditation (DDA) program
* LegitScript Healthcare Merchant Certification (HMC) Program`,
    type: ReasonInputType.Checkbox,
    document: MerchantDocumentsPendingApplication.legitScriptIdentifier,
    title: LEGITSCRIPT_REQUIRED_REASON_NAME,
    text: `The application states that the merchant expects some percentage of keyed sales. For keyed pharmacy sales, per Visa, the merchant must provide proof of one of the following certifications/accreditations in order to approve the merchant account. We’ll need confirmation that the merchant is accredited via one of the following programs:
* The National Association of Boards of Pharmacy (NABP) in either the Healthcare Merchant Accreditation or Drug Distributor Accreditation (DDA) program
* LegitScript Healthcare Merchant Certification (HMC) Program`,
    info: `All requested documents must be legible, include all pages, and be in the business name provided.`,
  },
  {
    name: 'Other Reason',
    description: `Other Reason`,
    type: ReasonInputType.RichText,
    document: MerchantDocumentsPendingApplication.otherDocuments,
    title: 'Other Reason',
    text: `Click here to upload`,
    info: `All requested documents must be legible, include all pages, and be in the business name provided.`,
  },
  {
    name: 'Ecom Ads',
    description: `<p>For sites with live traffic, please provide the ads/pics/videos that are actively running of the products sold on the site, advise us of the name of the Facebook page, or send us a link to the ad library. </p><br/>
<p>For sites with no traffic, please provide a screenshot of your Facebook Ad Manager account showing the "ads tab" displaying all created ads to be run and include screenshots of all other social media ads for the products sold on the site. </p>`,
    type: ReasonInputType.Checkbox,
    title: 'Ecom Ads',
    document: MerchantDocumentsPendingApplication.ecomAdsMaterials,
    text: `Click here to upload the ads/pics/videos that are actively running of the products sold on the site or a screenshot of your Facebook Ad Manager account`,
    info: `For sites with live traffic, please provide the ads/pics/videos that are actively running of the products sold on the site, advise us of the name of the Facebook page, or send us a link to the ad library.

    For sites with no traffic, please provide a screenshot of your Facebook Ad Manager account showing the "ads tab" displaying all created ads to be run and include screenshots of all other social media ads for the products sold on the site.`,
  },
];
